import { useMsal } from "@azure/msal-react";
import { PayloadAction } from "@reduxjs/toolkit";
import { getCurrentUser, setUserLogin } from "apis/authentication";
import SplashScreen from "components/SplashScreen/index";
import React, { useEffect, useReducer } from "react";
import { UserReducerType } from "redux/reducer-types";
import { useSetToastNotificationState } from "redux/toastNotification/toastNotificationSlice";
import { initialState as initialUserState, useGetUserState, useSetUserState } from "redux/user/userSlice";

const ACTION_TYPE = {
  INITIALISE: "INITIALISE",
  LOGIN: "LOGIN",
  LOGOUT: "LOGOUT",
};

const publicUrl = process.env.PUBLIC_URL || "/";

interface InitialAuthStateProps {
  isAuthenticated: boolean;
  isInitialised: boolean;
  role: string;
}

const initialAuthState: InitialAuthStateProps = {
  isAuthenticated: false,
  isInitialised: false,
  role: "",
};

const reducer = (state: InitialAuthStateProps | any, action: PayloadAction<Partial<InitialAuthStateProps>>) => {
  switch (action.type) {
    case ACTION_TYPE.INITIALISE: {
      const { isAuthenticated, role } = action.payload;
      return {
        ...state,
        isAuthenticated,
        isInitialised: true,
        role,
      };
    }
    case ACTION_TYPE.LOGIN: {
      const { isAuthenticated } = action.payload;
      return {
        ...state,
        isAuthenticated,
      };
    }
    case ACTION_TYPE.LOGOUT: {
      return {
        ...state,
        isAuthenticated: false,
      };
    }
    default: {
      return state;
    }
  }
};

const AuthContext = React.createContext({
  ...initialAuthState,
  logout: () => Promise,
});

interface IAuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: IAuthProviderProps) {
  const { instance } = useMsal();
  const [state, dispatch] = useReducer(reducer, initialAuthState);

  const { showErrorMessageFromResponse } = useSetToastNotificationState();
  const { setUserFields } = useSetUserState();
  const { checkUserLogin } = useGetUserState();

  const logout = () => {
    let user: UserReducerType = {
      displayName: "",
      mail: "",
      id: "",
      checkUserLogout: true,
      checkUserLogin: false,
    };
    setUserFields(user);
    instance
      .logoutPopup({
        postLogoutRedirectUri: publicUrl,
        mainWindowRedirectUri: publicUrl,
      })
      .finally(() => {
        localStorage.clear();
      });
    dispatch({ type: ACTION_TYPE.LOGOUT, payload: {} });
  };

  const initData = async () => {
    let isSuccess = false;
    let role = "";

    try {
      if (checkUserLogin) {
        await setUserLogin();
      }
      const currentUserResponse: any = await getCurrentUser();
      let userRedux: UserReducerType = {
        displayName: currentUserResponse?.displayName || "",
        mail: currentUserResponse?.displayName || "",
        id: currentUserResponse?.userId || "",
        checkUserLogout: false,
        checkUserLogin: false,
      };
      isSuccess = true;
      role = currentUserResponse?.group?.groupName || "";
      setUserFields(userRedux);
    } catch (error) {
      showErrorMessageFromResponse(error);
      setUserFields(initialUserState);
    } finally {
      dispatch({
        type: ACTION_TYPE.INITIALISE,
        payload: {
          isAuthenticated: isSuccess,
          role,
        },
      });
    }
  };

  useEffect(() => {
    initData();
  }, []);

  const valueContext = React.useMemo(
    () => ({
      ...state,
      logout,
    }),
    [state]
  );

  if (!state.isInitialised) {
    return <SplashScreen />;
  }

  return <AuthContext.Provider value={valueContext}>{children}</AuthContext.Provider>;
}

export default AuthContext;
